import { Injectable } from '@angular/core';
import { plainToClass } from 'class-transformer';
import { ConfigurationOrderApimodel } from '../../_models/api-models/configuration-order-apimodel';
import { ModelOrderApimodel } from '../../_models/api-models/model-order-apimodel';
import { ModelgroupOrderApimodel } from '../../_models/api-models/modelgroup-order-apimodel';
import { ModelsizeOrderApimodel } from '../../_models/api-models/modelsize-order-apimodel';
import { ProductlineOrderApimodel } from '../../_models/api-models/product-line';
import { DataTranslationModel } from '../../_models/data-translation-model';
import { OrderItemMiscState } from '../../_models/order-item-misc-model';
import { OrderItemState } from '../../_models/order-item-state';
import { OrderLineState } from '../../_models/order-line-state';
import { OrderState } from '../../_models/order-state';
import { ComponentReferencesV2 } from '../../_models/v2/component-references-v2';
import { OrderItemMiscStateV2 } from '../../_models/v2/order-item-misc-state-v2';
import { OrderItemStateV2 } from '../../_models/v2/order-item-state-v2';
import { OrderLineStateV2 } from '../../_models/v2/order-line-state-v2';
import { OrderStateV2 } from '../../_models/v2/order-state-v2';

@Injectable({
	providedIn: 'root'
})
export class OrderMapperService {

	constructor() { }

	mapItemToOriginal(source: OrderItemStateV2, componentRefs?: ComponentReferencesV2): OrderItemState | undefined {
		if (!source) {
			return undefined;
		}

		const result = new OrderItemState();

		result.Id = source.Id;
		result.Uuid = source.Uuid;
		result.OrderId = source.OrderId;
		result.CustomerNo = source.CustomerNo;
		result.Username = source.UserName;

		result.Reference = source.Reference;
		result.Engraving = source.Engraving;
		result.Amount = source.Amount;
		result.Comment = source.Comment;

		result.ProductLineUuid = source.ProductLineUuid;
		result.ProductLineCode = source.ProductLineCode;
		result.ModelCode = source.ModelCode;
		result.ModelGroupCode = source.ModelGroupCode;
		result.ModelSizeCode = source.ModelSizeCode;
		result.ModelConfigurationCode = source.ModelConfigurationCode;
		result.ModelGroupDescription = source.ModelGroupDescription;
		result.ModelConfigurationDescription = source.ModelConfigurationDescription;

		result.ModelUuid = source.ModelUuid;
		result.ModelGroupUuid = source.ModelGroupUuid;
		result.ModelSizeUuid = source.ModelSizeUuid;
		result.ConfigurationUuid = source.ConfigurationUuid;
		result.ModelConfigurationUuid = source.ModelConfigurationUuid;

		result.Demo = source.Demo;
		result.New = source.New;
		result.IsRimless = source.IsRimless;
		result.IsFullFrame = source.IsFullFrame;
		result.HasFixtures = source.HasFixtures;
		result.HasForms = source.HasForms;
		result.IsPrecious = source.IsPrecious;
		result.IsLimited = source.IsLimited;
		result.FirstTimeDelivery = source.FirstTimeDelivery;
		result.LowTempleInclinationPossible = source.LowTempleInclinationPossible;

		result.Age = source.Age;
		result.FirstPair = source.FirstPair as any;
		result.Gender = source.Gender as any;
		result.TempleInclination = source.TempleInclination;

		result.CaseItemNo = source.CaseItemNo;
		result.CaseItemVariantCode = source.CaseItemVariantCode;
		result.Parts = source.Parts;

		result.SerialNumber = source.SerialNumber || "";

		result.PriceJson = source.PriceJson;

		const yesterday = new Date();
		yesterday.setDate(yesterday.getDate() - 1);

		const isDiscontinued = function (discontinued: Date | null, date?: Date) {
			const today = date || new Date();
			return discontinued != null && discontinued <= today;
		};

		const productLine = {
			Discontinued: source.IsAnythingDiscontinued ? yesterday : null,
			Code: source.ProductLineCode,
			Description: new DataTranslationModel(`Description_ProductLine_${source.ProductLineCode}`, source.ProductLineCode),
			New: false,
			HasFixtures: source.HasFixtures,
			LineStatusMandatory: false,
			MinimumInitialPurchase: 0
		} as unknown as ProductlineOrderApimodel;

		productLine.isDiscontinued = (date?: Date) => isDiscontinued(productLine.Discontinued, date);

		result.OriginalProductLine = source.OriginalProductLine ? source.OriginalProductLine : productLine;

		const modelGroup = {
			Id: source.ModelGroupId || 0,
			Uuid: source.ModelGroupUuid || '',
			Code: source.ModelGroupCode,
			Description: new DataTranslationModel(`Description_ModelGroup_${source.ModelGroupCode}`, source.ModelGroupDescription),
			ShortDescription: new DataTranslationModel(`ShortDescription_ModelGroup_${source.ModelGroupCode}`, source.ModelGroupDescription),
			KidTeen: false,
			Status: 0,
			IsRimless: source.IsRimless,
			SortDescription: '',
			FullFrame: source.IsFullFrame,
			ProductLineCode: source.ProductLineCode,
			Discontinued: source.IsAnythingDiscontinued ? yesterday : null,
			New: false,
			HasForms: source.HasForms,
			HasFixtures: source.OriginalProductLine.HasFixtures,
			ProductLineUuid: source.ProductLineUuid,
			AssortmentLines: source.OriginalModelGroupAssortmentLines || []
		} as unknown as ModelgroupOrderApimodel;

		modelGroup.isDiscontinued = (date?: Date) => isDiscontinued(modelGroup.Discontinued, date);
		result.OriginalModelGroup = modelGroup;

		const model = {
			Id: source.ModelId || 0,
			Uuid: source.ModelUuid || '',
			Code: source.ModelCode,
			Order: '',
			ProductLineCode: source.ProductLineCode,
			LowTempleInclinationPossible: source.LowTempleInclinationPossible || 0,
			Discontinued: source.IsAnythingDiscontinued && !source.SerialNumber ? yesterday : null,
			New: false,
			AssortmentLines: source.OriginalModelAssortmentLines || []
		} as unknown as ModelOrderApimodel;

		model.isDiscontinued = (date?: Date) => isDiscontinued(model.Discontinued, date);
		result.OriginalModel = model;

		const modelSize = {
			Id: source.ModelSizeId || 0,
			Uuid: source.ModelSizeUuid || '',
			Code: source.ModelSizeCode,
			Discontinued: source.IsAnythingDiscontinued ? yesterday : null,
			New: false,
			Hbox: 0,
			Vbox: 0,
			Diameter: 0,
			Bridge: 0,
			Temple: 0,
			AssortmentLines: source.OriginalModelSizeAssortmentLines || []
		} as unknown as ModelsizeOrderApimodel;

		modelSize.isDiscontinued = (date?: Date) => isDiscontinued(modelSize.Discontinued, date);
		result.OriginalModelSize = modelSize;

		const configuration = {
			Id: source.ConfigurationId || 0,
			Uuid: source.ConfigurationUuid || '',
			Code: source.ModelConfigurationCode,
			Discontinued: source.IsAnythingDiscontinued ? yesterday : null,
			New: false,
			Release: new Date(),
			ModelConfigId: source.ModelConfigurationId || 0,
			Configuration: null,
			AssortmentLines: source.OriginalConfigurationAssortmentLines || []

		} as unknown as ConfigurationOrderApimodel;

		configuration.isDiscontinued = (date?: Date) => isDiscontinued(configuration.Discontinued, date);
		result.OriginalConfiguration = configuration;

		result.DiscountCode = source.DiscountCode;
		result.DiscountPercent = source.DiscountPercent;

		result.OrderLines = source.OrderLines?.map(line => this.mapLineToOriginal(line, componentRefs)).filter(Boolean) as OrderLineState[];

		return result;
	}

	mapMiscToOriginal(source: OrderItemMiscStateV2): OrderItemMiscState | undefined {
		if (!source) {
			return undefined;
		}

		const result = new OrderItemMiscState();

		result.Id = source.Id;
		result.Uuid = source.Uuid;
		result.OrderId = source.OrderId;
		result.CreatedAt = source.CreatedAt;
		result.CustomerNo = source.CustomerNo;

		result.Reference = source.Reference;
		result.Ecommerce = source.Ecommerce;
		result.EcommerceNoUnique = source.EcommerceNoUnique;
		result.EcommerceVariantCode = source.EcommerceVariantCode;
		result.Amount = source.Amount;

		result.Description = source.Description;
		result.ProductLineCode = source.ProductLineCode;

		result.KidTeen = source.KidTeen;
		result.ToolsIncluded = source.ToolsIncluded;
		result.Type = source.Type;

		result.DiscountCode = source.DiscountCode;
		result.DiscountPercent = source.DiscountPercentage;

		result.PriceJson = source.PriceJson;

		result.GrantProductLineStatus = source.GrantProductLineStatus;

		return result;
	}

	mapMiscFromOriginal(source: OrderItemMiscState): OrderItemMiscStateV2 | undefined {
		if (!source) {
			return undefined;
		}

		const result = new OrderItemMiscStateV2();

		result.Uuid = source.Uuid;
		result.OrderId = source.OrderId || 0;
		result.CreatedAt = source.CreatedAt;
		result.CustomerNo = source.CustomerNo || "";

		result.Reference = source.Reference || "";
		result.Ecommerce = source.Ecommerce || "";
		result.EcommerceNoUnique = source.EcommerceNoUnique || "";
		result.EcommerceVariantCode = source.EcommerceVariantCode || "";
		result.Amount = source.Amount || 0;

		result.Description = source.Description || "";
		result.ProductLineCode = source.ProductLineCode || "";

		result.KidTeen = source.KidTeen;
		result.ToolsIncluded = source.ToolsIncluded;
		result.Type = source.Type;

		result.DiscountCode = source.DiscountCode || "";
		result.DiscountPercentage = source.DiscountPercent || 0;

		result.PriceJson = source.PriceJson || "";

		return result;
	}

	mapLineToOriginal(source: OrderLineStateV2, componentRefs?: ComponentReferencesV2): OrderLineState | undefined {
		if (!source) {
			return undefined;
		}

		const result = new OrderLineState();

		result.Id = source.Id;
		result.Uuid = source.Uuid;

		result.ComponentVariantBypass = source.ComponentVariantBypass;
		result.ComponentSizeBypass = source.ComponentSizeBypass;
		result.SplittingData = source.SplittingData;
		result.Optional = source.Optional;
		result.EngravingPossible = source.EngravingPossible ?? 0;

		let componentVariantDescription = source.getVariantDescription(componentRefs);
		let componentSizeDescription = source.getSizeDescription(componentRefs);
		let colorDescription = source.getColorDescription(componentRefs);
		let colorGroupDescription = source.getColorGroupDescription(componentRefs);
		let componentDescription = source.getComponentDescription(componentRefs);

		result.ComponentVariantCode = source.ComponentVariantCode;
		result.ComponentSizeCode = source.ComponentSizeCode;
		result.ColorCode = source.ColorCode;
		result.ColorGroupCode = source.ColorGroupCode;
		result.ColorLineCode = source.ColorLineCode;
		result.ComponentCode = source.ComponentCode;

		result.ComponentVariantUuid = source.ComponentVariantUuid;
		result.ComponentSizeUuid = source.ComponentSizeUuid;
		result.ComponentColorUuid = source.ComponentColorUuid;
		result.ComponentUuid = source.ComponentUuid;

		result.ComponentVariantDescription = componentVariantDescription;
		result.ComponentSizeDescription = componentSizeDescription;
		result.ColorDescription = colorDescription;
		result.ColorGroupDescription = colorGroupDescription;
		result.ComponentDescription = componentDescription;

		const yesterday = new Date();
		yesterday.setDate(yesterday.getDate() - 1);

		const isDiscontinued = function (discontinued: Date | null, date?: Date) {
			const today = date || new Date();
			return discontinued != null && discontinued <= today;
		};

		const componentVariant = {
			Id: source.ComponentVariantId || 0,
			Discontinued: source.IsDiscontinued ? yesterday : null,
			New: source.IsNew,
			Code: source.ComponentVariantCode,
			Description: new DataTranslationModel(`Description_${source.ComponentVariantUuid}`, componentVariantDescription),
			EngravingPossible: source.EngravingPossible,
			ComponentCode: source.ComponentCode,
			AssortmentLines: []
		} as unknown as any;

		componentVariant.isDiscontinued = (date?: Date) => isDiscontinued(componentVariant.Discontinued, date);
		result.OriginalComponentVariant = componentVariant;

		const componentSize = {
			Id: source.ComponentSizeId || 0,
			Discontinued: source.IsDiscontinued ? yesterday : null,
			New: source.IsNew,
			Code: source.ComponentSizeCode,
			Size: source.ComponentSizeCode,
			Description: componentSizeDescription,
			Bypass: source.ComponentSizeBypass,
			AssortmentLines: [],
			Uuid: source.ComponentSizeUuid
		} as unknown as any;

		componentSize.isDiscontinued = (date?: Date) => isDiscontinued(componentSize.Discontinued, date);
		result.OriginalComponentSize = componentSize;

		const componentColor = {
			Id: source.ComponentColorId || 0,
			Discontinued: source.IsDiscontinued ? yesterday : null,
			Color: {
				Id: source.ColorId || 0,
				Code: source.ColorCode,
				Description: new DataTranslationModel(`Description_Color_${source.ComponentColorUuid}`, colorDescription),
				ColorGroup: {
					Code: source.ColorGroupCode,
					Description: new DataTranslationModel(`Description_ColorGroup_${source.ColorGroupCode}`, colorGroupDescription)
				}
			},
			AssortmentLines: [],
			Uuid: source.ComponentColorUuid
		} as unknown as any;

		componentColor.isDiscontinued = (date?: Date) => isDiscontinued(componentColor.Discontinued, date);
		result.OriginalComponentColor = componentColor;

		result.OriginalComponent = {
			Id: source.ComponentId || 0,
			Code: source.ComponentCode,
			Description: componentDescription,
			Uuid: source.ComponentUuid
		} as any;

		return result;
	}

	mapToOriginal(source: OrderStateV2): OrderState | undefined {
		if (!source) {
			return undefined;
		}

		const result = new OrderState();

		result.Id = source.Id;
		result.Uuid = source.Uuid;
		result.AxId = source.AxId;
		result.CreatedAt = source.CreatedAt;
		result.CompletedDate = source.CompletedDate;

		result.OrderItems = source.OrderItems?.map(item => this.mapItemToOriginal(item, source.ComponentReferences)).filter(Boolean) as OrderItemState[];
		result.OrderItemMiscs = source.OrderItemMiscs?.map(item => this.mapMiscToOriginal(item)).filter(Boolean) as OrderItemMiscState[];
		result.Status = source.Status;

		result.CustomerNo = source.CustomerNo;
		result.CustomerName = source.CustomerName;
		result.CustomerEmail = source.CustomerEmail;
		result.CheckedOrderConfirmationEmailAddressesString = source.CheckedOrderConfirmationEmailAddressesString;
		result.Alias = source.Alias;

		result.Address = source.Address;
		result.FullAddress = source.FullAddress || source.Address;
		result.ShipToId = source.ShipToId;

		result.TestOrder = source.TestOrder;
		result.AllowPartialShipment = source.AllowPartialShipment;
		result.NoVisit = source.NoVisit;

		result.Notes = source.Notes;
		result.NotesCustomerService = source.NotesCustomerService;
		result.PaymentTerms = source.PaymentTerms;
		result.CrmTicketId = source.CrmTicketId;
		result.Caution = source.Caution;
		result.Signature = source.Signature;
		result.ShopInfos = source.ShopInfos;

		result.FairId = source.FairId;
		result.FairName = source.FairName;

		result.EarliestDeliveryDate = source.EarliestDeliveryDate;
		result.VisitDate = source.VisitDate;

		result.ConsultantUuid = source.ConsultantUuid;

		result.ShowOrderConfirmation = false;
		result.Virtual = false;
		result.ConfirmationEmails = [];
		result.CheckedOrderConfirmationEmailAddresses =
			source.CheckedOrderConfirmationEmailAddressesString?.split(',') || [];

		return result;
	}

	mapToOriginalList(source: OrderStateV2[]): OrderState[] {
		if (!source) {
			return [];
		}

		return source.map(item => this.mapToOriginal(item)).filter(Boolean) as OrderState[];
	}

	plainToOrderStateV2(source: any): OrderStateV2 {
		return plainToClass(OrderStateV2, source);
	}

	plainToOrderStateV2List(source: any[]): OrderStateV2[] {
		if (!source) {
			return [];
		}

		return source.map(item => this.plainToOrderStateV2(item));
	}

}
